import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { PlayCircleFilled, MonetizationOn, Token, Campaign, AttachMoney, TrendingUp } from '@mui/icons-material';

const AdExplainedSection = () => {
    const theme = useTheme();

    return (
        <Box id="features" sx={{ py: 10, position: 'relative' }}>
            <Container maxWidth="lg"> {/* Reverted to lg to fix stretching */}
                <Grid container spacing={8}>
                    {/* Viewers Side */}
                    <Grid item xs={12} md={6}>
                        <Box
                            component={motion.div}
                            initial={{ opacity: 0, y: 30 }} // Vertical only
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <Typography variant="h6" sx={{ color: theme.palette.primary.main, mb: 1, fontWeight: 700, letterSpacing: '1px' }}>
                                FOR VIEWERS
                            </Typography>
                            <Typography variant="h3" sx={{ color: theme.palette.text.primary, mb: 4, fontWeight: 800 }}>
                                Watch, Earn & <br /><span style={{ color: theme.palette.secondary.main }}>Redeem</span>
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <FeatureBlock
                                    icon={<PlayCircleFilled sx={{ color: theme.palette.primary.main, fontSize: 30 }} />}
                                    title="Watch Ads"
                                    desc="Browse and watch curated ads from our partner projects."
                                    delay={0.1}
                                    theme={theme}
                                />
                                <FeatureBlock
                                    icon={<Token sx={{ color: theme.palette.primary.main, fontSize: 30 }} />}
                                    title="Collect Points"
                                    desc="Earn points for every second of attention you give."
                                    delay={0.2}
                                    theme={theme}
                                />
                                <FeatureBlock
                                    icon={<MonetizationOn sx={{ color: theme.palette.primary.main, fontSize: 30 }} />}
                                    title="Redeem Tokens"
                                    desc="Convert your collected points into Trend Tokens instantly based on the current price."
                                    delay={0.3}
                                    theme={theme}
                                />
                            </Box>
                        </Box>
                    </Grid>

                    {/* Advertisers Side */}
                    <Grid item xs={12} md={6}>
                        <Box
                            component={motion.div}
                            initial={{ opacity: 0, y: 30 }} // Vertical only
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }} // Slight delay for stagger
                        >
                            <Typography variant="h6" sx={{ color: theme.palette.secondary.main, mb: 1, fontWeight: 700, letterSpacing: '1px' }}>
                                FOR ADVERTISERS
                            </Typography>
                            <Typography variant="h3" sx={{ color: theme.palette.text.primary, mb: 4, fontWeight: 800 }}>
                                Pay Fee & <br /><span style={{ color: theme.palette.primary.main }}>Grow Traffic</span>
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <FeatureBlock
                                    icon={<AttachMoney sx={{ color: theme.palette.secondary.main, fontSize: 30 }} />}
                                    title="Pay The Fee"
                                    desc="Clients pay a transparent fee to list their business or product."
                                    delay={0.3}
                                    theme={theme}
                                />
                                <FeatureBlock
                                    icon={<Campaign sx={{ color: theme.palette.secondary.main, fontSize: 30 }} />}
                                    title="Ad Goes Live"
                                    desc="Your campaign is instantly distributed to thousands of active Web3 viewers."
                                    delay={0.4}
                                    theme={theme}
                                />
                                <FeatureBlock
                                    icon={<TrendingUp sx={{ color: theme.palette.secondary.main, fontSize: 30 }} />}
                                    title="Capture Value"
                                    desc="Get real engagement from users who are rewarded to listen."
                                    delay={0.5}
                                    theme={theme}
                                />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

const FeatureBlock = ({ icon, title, desc, delay, theme }) => (
    <Box
        component={motion.div}
        initial={{ opacity: 0, y: 15 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: delay, duration: 0.4 }}
        sx={{
            display: 'flex',
            gap: 2,
            p: 3,
            borderRadius: '16px',
            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.6)',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
            transition: 'all 0.3s',
            '&:hover': {
                background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)',
                transform: 'translateX(10px)',
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.15)'
            }
        }}
    >
        <Box sx={{ mt: 0.5 }}>{icon}</Box>
        <Box>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 700, fontSize: '1.1rem' }}>{title}</Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>{desc}</Typography>
        </Box>
    </Box>
);

export default AdExplainedSection;
